import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSideConversationContext } from "../../contexts/SideConversationContext";
import { zafClient, popupSuccess, popupError } from "../../services/Zendesk";

export interface ProductData {
  id: string; // ID do incident/ticket (para deletar)
  incidentId: string; // ID do incident/ticket (backup/clareza)
  name: string;
  quantidade: string;
  preco: string;
  produtoId: string; // ID do custom object produto
  codigo?: string;
  marca?: string;
  linha?: string;
  peso?: string;
  altura?: string;
  largura?: string;
  profundidade?: string;
  ativo?: string;
  produtoComercial?: string;
  familiaProduto?: string;
  codigoErp?: string;
  imageUrl?: string;
}

// Interface para dados do pedido/side conversation
export interface OrderData {
  id: string;
  title: string;
  subject: string;
  state: string;
  createdAt: string;
  // Campos do side conversation para exibir no accordion principal
  sideConversationFields: string[];
  sideConversationValues: string[];
  products: ProductData[];
}

export function useOrder() {
  const navigate = useNavigate();
  const { sideConversationId } = useParams<{ sideConversationId: string }>();
  const { getSideConversationData, removeProductFromSideConversation } =
    useSideConversationContext();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log("🔄 useOrder renderizado com:", {
    sideConversationId,
    orderData: !!orderData,
    isLoading,
    error,
  });

  const loadOrderData = () => {

    if (!sideConversationId) {
      setError("ID da side conversation não encontrado");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log(
        "🔍 Buscando dados no contexto para side conversation:",
        sideConversationId
      );

      // Buscar dados do contexto
      const contextData = getSideConversationData(sideConversationId);

      if (!contextData) {
        setError(
          "Dados do pedido não encontrados. Volte para a Home e tente novamente."
        );
        setIsLoading(false);
        return;
      }


      // Converter dados do contexto para o formato da página Order
      const orderData: OrderData = {
        id: contextData.id,
        title: contextData.title,
        subject: contextData.subject,
        state: contextData.state,
        createdAt: contextData.createdAt,
        sideConversationFields: contextData.fields,
        sideConversationValues: contextData.values,
        products: contextData.products,
      };


      setOrderData(orderData);
      setError(null);
    } catch (error) {
      console.error("Erro ao processar dados do contexto:", error);
      setError("Erro ao processar dados do pedido");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async (productId: string) => {
    if (!productId) {
      popupError("ID do produto não encontrado", false);
      return;
    }

    
    // Buscar o produto correspondente para obter o incidentId correto
    const productToDelete = orderData?.products.find(p => p.id === productId);
    if (!productToDelete) {
      console.error("❌ Produto não encontrado na lista:", productId);
      popupError("Produto não encontrado", false);
      return;
    }

    console.log("🔍 Produto encontrado:", {
      id: productToDelete.id,
      incidentId: productToDelete.incidentId,
      name: productToDelete.name,
      produtoId: productToDelete.produtoId
    });

    // Usar o incidentId para deletar o ticket
    const incidentIdToDelete = productToDelete.incidentId || productToDelete.id;
    console.log("�️ ID do incident que será deletado:", incidentIdToDelete);    try {
      
      // Usar soft delete - o ticket vai para deleted tickets mas pode ser restaurado
      await zafClient.request({
        url: `/api/v2/tickets/${incidentIdToDelete}.json`,
        type: "DELETE",
      });


      popupSuccess("Produto removido com sucesso!");

      // Atualizar a lista local removendo o produto deletado
      if (orderData && sideConversationId) {
        const updatedProducts = orderData.products.filter(
          (p) => p.id !== productId
        );
        setOrderData({
          ...orderData,
          products: updatedProducts,
        });
        console.log(
          `📋 Produto ${productId} removido da lista local. Produtos restantes: ${updatedProducts.length}`
        );

        // Também atualizar o contexto para manter os dados sincronizados
        removeProductFromSideConversation(sideConversationId, productId);
      }
    } catch (error) {
      console.error("❌ Erro ao deletar incident:", error);

      // Extrair mensagem de erro mais específica se disponível
      const errorMessage =
        (error as any)?.response?.data?.description ||
        (error as any)?.message ||
        "Erro ao remover produto";

      popupError(`Erro ao remover produto: ${errorMessage}`, false);
    }
  };

  useEffect(() => {
    loadOrderData();
  }, [sideConversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    navigate,
    orderData,
    isLoading,
    error,
    refetch: loadOrderData,
    deleteProduct,
  };
}
